import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity,
  Users,
  Calendar,
  Handshake,
  Music as MusicIcon,
  Loader2,
  RefreshCw,
  TrendingUp,
  Heart,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, primary, delta, color = 'red', testId }) => {
  const colorMap = {
    red: 'from-accent-red/20 to-accent-red/5 border-accent-red/40',
    amber: 'from-amber-400/20 to-amber-400/5 border-amber-400/40',
    cyan: 'from-cyan-400/20 to-cyan-400/5 border-cyan-400/40',
    purple: 'from-purple-400/20 to-purple-400/5 border-purple-400/40',
  };
  return (
    <div
      className={`bg-gradient-to-br ${colorMap[color]} border rounded-xl p-5 flex items-start gap-4`}
      data-testid={testId}
    >
      <div className="bg-gunmetal-900/60 rounded-lg p-3">
        <Icon size={22} className="text-steel-100" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.25em] uppercase text-steel-400 mb-1">
          {label}
        </div>
        <div className="text-3xl font-black text-steel-100 leading-none">{primary}</div>
        {delta && (
          <div className="mt-1.5 text-xs text-steel-400 flex items-center gap-1">
            <TrendingUp size={12} className="text-amber-400" />
            {delta}
          </div>
        )}
      </div>
    </div>
  );
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

export const AdminHeartbeat = ({ adminToken }) => {
  const [data, setData] = useState(null);
  const [mostLoved, setMostLoved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [hbRes, lovedRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/heartbeat`, {
          headers: { 'X-Admin-Token': adminToken },
        }),
        fetch(`${API_URL}/api/music/most-loved?limit=8`),
      ]);
      if (!hbRes.ok) throw new Error(`Heartbeat failed (${hbRes.status})`);
      const json = await hbRes.json();
      setData(json);
      if (lovedRes.ok) {
        const lovedJson = await lovedRes.json();
        setMostLoved(lovedJson.songs || []);
      }
      setRefreshedAt(new Date());
    } catch (e) {
      setErr(e.message || 'Could not load Heartbeat.');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) load();
  }, [adminToken, load]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!adminToken) return undefined;
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [adminToken, load]);

  if (loading && !data) {
    return (
      <div
        className="bg-gunmetal-900/60 border border-steel-700/40 rounded-xl p-8 mb-8 flex items-center gap-3 text-steel-400"
        data-testid="heartbeat-loading"
      >
        <Loader2 className="animate-spin" size={20} />
        Loading the MINDESIGN heartbeat…
      </div>
    );
  }
  if (err) {
    return (
      <div
        className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 mb-8 text-red-300 text-sm"
        data-testid="heartbeat-error"
      >
        Heartbeat: {err}
      </div>
    );
  }
  if (!data) return null;

  const { newsletter, bookings, partners, music } = data;

  return (
    <div className="mb-8" data-testid="admin-heartbeat">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Activity className="text-accent-red" size={22} />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-accent-red animate-pulse"></span>
          </div>
          <div>
            <h2 className="text-2xl font-black tracking-wide text-steel-100">
              MINDESIGN HEARTBEAT
            </h2>
            <p className="text-xs text-steel-500">
              Auto-refreshes every 60s · Last update {refreshedAt ? formatTime(refreshedAt.toISOString()) : '—'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="flex items-center gap-2 text-xs tracking-widest uppercase text-steel-400 hover:text-accent-red transition-colors"
          data-testid="heartbeat-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Users}
          label="Subscribers"
          primary={newsletter.total}
          delta={`+${newsletter.today} today · +${newsletter.this_week} this week`}
          color="amber"
          testId="stat-subscribers"
        />
        <StatCard
          icon={Calendar}
          label="Booking Inquiries"
          primary={bookings.total}
          delta={`+${bookings.today} today · ${bookings.new} unread`}
          color="red"
          testId="stat-bookings"
        />
        <StatCard
          icon={Handshake}
          label="Active Partners"
          primary={partners.active}
          delta={partners.total > partners.active ? `${partners.total - partners.active} inactive` : 'all active'}
          color="cyan"
          testId="stat-partners"
        />
        <StatCard
          icon={MusicIcon}
          label="Total Plays · Radio"
          primary={music.total_plays}
          delta={`${music.songs} songs in rotation`}
          color="purple"
          testId="stat-music"
        />
      </div>

      {/* Activity lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent subscribers */}
        <div className="bg-gunmetal-900/60 border border-steel-700/30 rounded-xl p-5" data-testid="recent-subscribers">
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-amber-400" />
            <h3 className="text-xs tracking-[0.25em] uppercase text-amber-400 font-bold">
              Recent Subscribers
            </h3>
          </div>
          {newsletter.recent.length === 0 ? (
            <p className="text-steel-500 text-sm">No subscribers yet.</p>
          ) : (
            <ul className="space-y-2">
              {newsletter.recent.map((s) => (
                <li
                  key={s.email}
                  className="text-sm text-steel-300 flex items-baseline justify-between gap-2 border-b border-steel-800/40 pb-1.5 last:border-0"
                >
                  <span className="truncate">
                    <span className="text-steel-100 font-medium">{s.name || '—'}</span>{' '}
                    <span className="text-steel-500">·</span>{' '}
                    <span className="text-steel-400">{s.email}</span>
                  </span>
                  <span className="text-xs text-steel-600 whitespace-nowrap">
                    {formatTime(s.subscribed_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent bookings */}
        <div className="bg-gunmetal-900/60 border border-steel-700/30 rounded-xl p-5" data-testid="recent-bookings">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-accent-red" />
            <h3 className="text-xs tracking-[0.25em] uppercase text-accent-red font-bold">
              Recent Bookings
            </h3>
          </div>
          {bookings.recent.length === 0 ? (
            <p className="text-steel-500 text-sm">No booking inquiries yet.</p>
          ) : (
            <ul className="space-y-2">
              {bookings.recent.map((b) => (
                <li
                  key={b.id}
                  className="text-sm text-steel-300 border-b border-steel-800/40 pb-1.5 last:border-0"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-steel-100 font-medium truncate">{b.name}</span>
                    <span className="text-xs text-steel-600 whitespace-nowrap">
                      {formatTime(b.created_at)}
                    </span>
                  </div>
                  <div className="text-xs text-steel-500 truncate">
                    {b.location || 'Location TBD'}{' '}
                    <span className="text-steel-600">·</span>{' '}
                    <span className="font-mono text-amber-400/80">{b.id}</span>
                    {b.status === 'new' && (
                      <span className="ml-1.5 inline-flex items-center text-[9px] tracking-widest uppercase px-1.5 py-0.5 bg-accent-red/20 text-accent-red rounded-sm">
                        New
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top songs */}
        <div className="bg-gunmetal-900/60 border border-steel-700/30 rounded-xl p-5" data-testid="top-songs">
          <div className="flex items-center gap-2 mb-3">
            <MusicIcon size={14} className="text-purple-400" />
            <h3 className="text-xs tracking-[0.25em] uppercase text-purple-400 font-bold">
              Top On The Radio
            </h3>
          </div>
          {music.top.length === 0 ? (
            <p className="text-steel-500 text-sm">No plays logged yet.</p>
          ) : (
            <ol className="space-y-2">
              {music.top.map((s, i) => (
                <li
                  key={s.id}
                  className="text-sm text-steel-300 flex items-baseline justify-between gap-2 border-b border-steel-800/40 pb-1.5 last:border-0"
                >
                  <span className="truncate flex items-baseline gap-2">
                    <span className="text-steel-600 text-xs w-4">{i + 1}.</span>
                    <span className="text-steel-100 font-medium truncate">{s.title}</span>
                  </span>
                  <span className="text-xs text-amber-400 font-bold whitespace-nowrap">
                    {s.plays || 0} ▶
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Most Loved Tracks */}
      <div className="bg-gunmetal-900/60 border border-pink-500/30 rounded-xl p-5 mt-4" data-testid="most-loved">
        <div className="flex items-center gap-2 mb-3">
          <Heart size={14} className="text-pink-400 fill-pink-400" />
          <h3 className="text-xs tracking-[0.25em] uppercase text-pink-400 font-bold">
            Most Loved Tracks · Listener Hearts
          </h3>
        </div>
        {mostLoved.length === 0 ? (
          <p className="text-steel-500 text-sm">
            No hearts yet. Listeners can like a track from the floating radio — those hearts land here in real time.
          </p>
        ) : (
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {mostLoved.map((s, i) => (
              <li
                key={s.id}
                className="text-sm text-steel-300 flex items-baseline justify-between gap-2 border-b border-steel-800/40 pb-1.5 last:border-0"
                data-testid={`most-loved-row-${s.id}`}
              >
                <span className="truncate flex items-baseline gap-2">
                  <span className="text-steel-600 text-xs w-4">{i + 1}.</span>
                  <span className="text-steel-100 font-medium truncate">{s.title}</span>
                  <span className="text-xs text-steel-600 truncate">· {s.genre}</span>
                </span>
                <span className="text-xs text-pink-400 font-bold whitespace-nowrap flex items-center gap-1">
                  <Heart size={10} className="fill-pink-400" />
                  {s.likes}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

export default AdminHeartbeat;
